<template>
  <div
    class="image-list"
    @keydown="handleKeyNavigation"
    @keyup="handleKeyNavigation"
    tabindex="0"
    ref="imageListRef"
  >
    <div v-for="(row, rowIndex) in imageRows" :key="rowIndex" class="image-row">
      <div
        v-for="(image, colIndex) in row"
        :key="`${rowIndex}-${colIndex}`"
        class="image-container"
        :class="{
          active: isActiveImage(rowIndex, colIndex),
          placeholder: !image,
          last: rowIndex === imageRows.length - 1 && row.length === 1,
        }"
        @click="image && selectImage(rowIndex, colIndex)"
      >
        <img
          v-if="image"
          :src="image.thumbnailUrl"
          :alt="image.filename"
          :id="`image-thumbnail-${rowIndex * 2 + colIndex}`"
          class="image-thumbnail"
          @load="onImageLoad($event, rowIndex, colIndex)"
        />
        <div
          v-else
          class="placeholder-content"
          :style="{ height: placeholderHeight + 'px' }"
        >
          <span>{{ $t("imageNavigation.loading") }}</span>
        </div>
        <div v-if="image" class="image-info-overlay">
          <div class="filename">
            <p>{{ image.filename.split(".").slice(0, -1).join(".") }}</p>
          </div>
          <div class="date-taken">
            <p>
              {{
                image.metadata?.dateTaken
                  ? formatDate(image.metadata.dateTaken)
                  : ""
              }}
            </p>
          </div>
        </div>
        <img
          v-if="image && image.coordinatesOnImage"
          src="@/assets/icons/pinpoint.svg"
          alt="highlight point"
          class="highlight-point"
          :style="getHighlightPointStyle(image)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { debounce } from "lodash";
import { formatDate } from "@/utils/date-utils";

const props = defineProps([
  "images",
  "bestImage",
  "fullScreenImageActive",
  "originalImageWidth",
  "originalImageHeight",
]);

const emit = defineEmits([
  "image-selected",
  "scroll-stopped",
  "page-size-calculated",
  "setupBesPopUpImage",
  "best-image-changed",
  "image-loading-state",
]);

const imageListRef = ref(null);
const activeRow = ref(0);
const activeCol = ref(0);
const rowHeight = ref(0);

const previousImage = () => {
  if (activeCol.value > 0) {
    activeCol.value--;
  } else if (activeRow.value > 0) {
    activeRow.value--;
    activeCol.value = imageRows.value[activeRow.value].length - 1;
  }
  setTimeout(() => {
    const currentImage = imageRows.value[activeRow.value][activeCol.value];
    emit("image-selected", currentImage);

    // If the image doesn't exist or is still loading, emit a loading state
    if (!currentImage && props.fullScreenImageActive) {
      emit("image-loading-state", true);
    }

    scrollSelectedIntoView();
  }, 0);
};

const nextImage = () => {
  if (activeCol.value < 1) {
    activeCol.value++;
  } else if (activeRow.value < imageRows.value.length - 1) {
    activeRow.value++;
    activeCol.value = 0;
  }
  setTimeout(() => {
    const currentImage = imageRows.value[activeRow.value][activeCol.value];
    emit("image-selected", currentImage);

    // If the image doesn't exist or is still loading, emit a loading state
    if (!currentImage && props.fullScreenImageActive) {
      emit("image-loading-state", true);
    }

    scrollSelectedIntoView();
  }, 0);
};

defineExpose({
  previousImage,
  nextImage,
});

// Add a ref to store thumbnail dimensions
const thumbnailSizes = ref({});

const imageRows = computed(() => {
  const rows = [];

  for (let i = 0; i < props.images.length; i += 2) {
    rows.push(props.images.slice(i, i + 2));
  }
  return rows;
});

const isActiveImage = (row, col) => {
  return activeRow.value === row && activeCol.value === col;
};

const selectImage = (row, col) => {
  activeRow.value = row;
  activeCol.value = col;
  emitSelectedImage();
};

const scrollSelectedIntoView = () => {
  if (!imageListRef.value) return;

  const activeElement = imageListRef.value.querySelector(
    ".image-container.active"
  );

  if (activeElement) {
    activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const handleKeyNavigation = (event) => {
  if (event.type === "keydown") {
    // Prevent default scrolling behavior when arrow keys are pressed
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      event.preventDefault();
    }

    switch (event.key) {
      case "ArrowRight":
        if (imageRows.value[activeRow.value].length - 1 > activeCol.value) {
          activeCol.value++;
        } else if (activeRow.value < imageRows.value.length - 1) {
          activeRow.value++;
          activeCol.value = 0;
        }
        break;
      case "ArrowLeft":
        if (activeCol.value > 0) {
          activeCol.value--;
        } else if (activeRow.value > 0) {
          activeRow.value--;
          activeCol.value = imageRows.value[activeRow.value].length - 1;
        }
        break;
      case "ArrowUp":
        if (activeRow.value > 0) {
          activeRow.value--;
        }
        break;
      case "ArrowDown":
        if (imageRows.value.length - 1 > activeRow.value) {
          activeRow.value++;
          if (activeCol.value > imageRows.value[activeRow.value].length - 1) {
            activeCol.value = imageRows.value[activeRow.value].length - 1;
          }
        }
        break;
    }
  } else if (event.type === "keyup") {
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      const currentImage = imageRows.value[activeRow.value][activeCol.value];
      emit(
        "image-selected",
        currentImage,
        props.fullScreenImageActive ? true : false
      );
      scrollSelectedIntoView();
    }
  }
};

const getFirstVisibleIndex = () => {
  if (!imageListRef.value) return 0;

  const scrollTop = imageListRef.value.scrollTop;
  const firstVisibleRow = Math.floor(scrollTop / rowHeight.value);

  return firstVisibleRow * 2;
};

const calculateVisibleImages = () => {
  if (!imageListRef.value) return 0;

  const containerHeight = imageListRef.value.clientHeight;

  const imageRows = document.getElementsByClassName("image-row");
  if (imageRows.length > 0) {
    const rowElement = imageRows[0];
    const computedStyle = window.getComputedStyle(rowElement);
    rowHeight.value =
      rowElement.offsetHeight + parseInt(computedStyle.marginBottom || 0);
  } else {
    rowHeight.value = imageListRef.value.clientWidth / 2 + 8;
  }

  const visibleRows = Math.ceil(containerHeight / rowHeight.value);

  const calculatedVisibleImages = visibleRows * 2;

  return calculatedVisibleImages;
};

const emitSelectedImage = debounce(() => {
  const currentImage = imageRows.value[activeRow.value][activeCol.value];
  // Start loading high-res image after 200ms of no activity but this might not bee needed, we can load it with same function which is emitter
  emit("image-selected", currentImage);
}, 200);

const handleScrollStop = debounce(() => {
  const topIndex = getFirstVisibleIndex();
  emit("scroll-stopped", topIndex);
}, 200);

const setInitialSelection = (index) => {
  if (index === undefined || index === null) {
    emit("setupBesPopUpImage", index);
    return;
  }

  // Calculate row and column from the index
  activeRow.value = Math.floor(index / 2);
  activeCol.value = index % 2;

  // Emit the initially selected image
  if (imageRows.value[activeRow.value][activeCol.value]) {
    emit(
      "image-selected",
      imageRows.value[activeRow.value][activeCol.value],
      false
    );
  }

  if (typeof index === "number") {
    emit("setupBesPopUpImage", index);
  }

  // Wait for the DOM to update before scrolling
  setTimeout(() => {
    scrollSelectedIntoView();
  }, 0);
};

// Add watcher for bestimage
watch(
  () => props.bestImage,
  (newIndex) => {
    setInitialSelection(newIndex);

    if (newIndex !== undefined && newIndex !== null && newIndex >= 0) {
      setTimeout(() => {
        emit("best-image-changed", newIndex);
      }, 1000);
    }
  }
);

const placeholderHeightSet = ref(false);
const placeholderHeight = ref(89);

// Function to handle image load and store its dimensions
const onImageLoad = (event, rowIndex, colIndex) => {
  const thumbnailImg = event.target;
  const originalImg = new Image();

  originalImg.onload = () => {
    thumbnailSizes.value[`${rowIndex}-${colIndex}`] = {
      thumbnailWidth: thumbnailImg.clientWidth,
      thumbnailHeight: thumbnailImg.clientHeight,
      originalWidth: originalImg.width,
      originalHeight: originalImg.height,
    };
    if (!placeholderHeightSet.value) {
      placeholderHeightSet.value = true;
      placeholderHeight.value = thumbnailImg.clientHeight;
    }
  };

  originalImg.src = thumbnailImg.src;

  // Add this check to update the selected image if it just loaded
  // and user is currently viewing this position
  if (
    activeRow.value === rowIndex &&
    activeCol.value === colIndex &&
    props.fullScreenImageActive
  ) {
    emit("image-selected", imageRows.value[rowIndex][colIndex], true);
  }
};

// Function to get highlight point style
const getHighlightPointStyle = (image) => {
  const imageElement = document.querySelector(`.image-thumbnail`);
  if (
    !imageElement ||
    !image.coordinatesOnImage ||
    imageElement.clientWidth === 0
  ) {
    return {
      visibility: "hidden",
    };
  }

  const originalPoint = {
    x: image.coordinatesOnImage.x * props.originalImageWidth,
    y: image.coordinatesOnImage.y * props.originalImageHeight,
  };

  const thumbPoint = scalePointToThumbnail(imageElement, originalPoint);

  const pointDisplayed = scaleThumbnailToDisplayed(imageElement, thumbPoint);

  return {
    visibility: "visible",
    left: `${pointDisplayed.x}px`,
    top: `${pointDisplayed.y}px`,
    transform: `translate(-50%, -50%)`,
  };
};

const scalePointToThumbnail = function (image, point) {
  const thumbnailWidth = image.naturalWidth;
  const thumbnailHeight = image.naturalHeight;

  const scaleToThumbnailX = thumbnailWidth / props.originalImageWidth;
  const scaleToThumbnailY = thumbnailHeight / props.originalImageHeight;

  return {
    x: point.x * scaleToThumbnailX,
    y: point.y * scaleToThumbnailY,
  };
};

const scaleThumbnailToDisplayed = function (image, pointThumbnail) {
  const thumbnailWidth = image.naturalWidth;
  const thumbnailHeight = image.naturalHeight;

  const containerWidth = image.clientWidth;
  const containerHeight = image.clientHeight;

  const thumbnailAspect = thumbnailWidth / thumbnailHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight, offsetX, offsetY;

  if (containerAspect > thumbnailAspect) {
    displayedHeight = containerHeight;
    displayedWidth = displayedHeight * thumbnailAspect;
    offsetX = (containerWidth - displayedWidth) / 2;
    offsetY = 0;
  } else {
    displayedWidth = containerWidth;
    displayedHeight = displayedWidth / thumbnailAspect;
    offsetX = 0;
    offsetY = (containerHeight - displayedHeight) / 2;
  }

  const scaleX = displayedWidth / thumbnailWidth;
  const scaleY = displayedHeight / thumbnailHeight;

  const pointDisplayed = {
    x: pointThumbnail.x * scaleX + offsetX,
    y: pointThumbnail.y * scaleY + offsetY,
  };

  return pointDisplayed;
};

onMounted(async () => {
  imageListRef.value.addEventListener("scroll", handleScrollStop);
  imageListRef.value.focus();

  setTimeout(() => {
    const recommendedPageSize = calculateVisibleImages();
    emit("page-size-calculated", recommendedPageSize);

    // Call setInitialSelection after calculating page size
    setInitialSelection();
  }, 0);
});

onUnmounted(() => {
  if (imageListRef.value) {
    imageListRef.value.removeEventListener("scroll", handleScrollStop);
  }
});
</script>

<style lang="scss" scoped>
.image-list {
  padding: 16px;
  outline: none;
  height: calc(100vh - 120px);
  overflow-y: auto;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: var(--color-text-subtle);
}

.image-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.image-container {
  flex: 1;
  cursor: pointer;
  border: 2px solid transparent;
  overflow: hidden;
  transition: border-color 0.2s ease;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
  .highlight-point {
    position: absolute;
    width: 10px;
    height: 10px;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &.active {
    border-color: var(--color-gree300);
  }

  &.last {
    flex: 0.5;
    margin-right: 8px;
  }

  &.placeholder {
    background-color: var(--color-hover-bg);
    cursor: default;
    display: flex;
    align-items: center;
    justify-content: center;

    .placeholder-content {
      color: white;
      font-size: 14px;
      align-content: center;
    }
  }

  .image-info-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    color: white;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 8px;
    background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
  }

  &:hover .image-info-overlay {
    opacity: 1;
  }

  .filename {
    p {
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
    }
  }
  .date-taken {
    opacity: 1;
    p {
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
    }
  }
}
</style>
